import '../input/select-input';
import '../input/button-input';

import{ html, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { cartStore } from '../../_stores/cart-store';

import { FormElement } from './form';

export interface Variant {
    id: string;
    title: string;
    isDefault: boolean;
    hasStock: boolean;
    priceAsCurrency: string;
	availableForPurchase: boolean;
}

@customElement('product-form')
export class ProductForm extends FormElement {

    // Props

    @property({ type: Array })
    variants: Variant[] = [];

    @state()
    _crsf: string = '';

    @state()
    _activeVariant: Variant | null = null;

    @state()
    private _busy: boolean = false;
    

    // Lifecycle Hooks

    firstUpdated() {

        // add classes
        this.classList.add('Form','Form--product');

        // handle slots
        const slot: HTMLElement | null = this.querySelector('[slot="crsf"]');
        if (slot) {
            this._crsf = slot.innerHTML;
            slot.remove();
        }

        // get active variant
        this._activeVariant = (this.variants.length > 1) ? null : this._defaultVariant;
    }

    // Render

    render() {
        return html`
            <form method="POST" enctype="multipart/form-data">
                ${ unsafeHTML(this._crsf) }
                <div class="Form-body">
                    ${ (this._availableForPurchase) ? this.priceDisplayTemplate() : '' }
                    ${ (this.variants.length > 1) 
                        ? this.selectInputTemplate() 
                        : this.hiddenInputTemplate('purchasableId', this._defaultVariant.id) }
                    ${ this._availableForPurchase 
						? this.submitButtonTemplate(
							'addToCart',
							this._soldOut ? 'Sold Out' : 'Add to Cart',
							this._busy || this._soldOut || !this._activeVariant) 
						: '' }
                </div>
            </form>
        `;
    }

    createRenderRoot() { 
        return this;
    }

    // Templates

    priceDisplayTemplate(): TemplateResult {
        return html`
            <div class="Form-block">
                <p>
                    ${ (this._activeVariant) 
                        ? this._activeVariant.priceAsCurrency 
                        : this._defaultVariant.priceAsCurrency }
                </p>
            </div>
        `;
    }

    selectInputTemplate(): TemplateResult {
        return html`
            <div class="Form-block">
                <select-input
                    name="purchasableId"
                    .options="${ this.variants.map((variant) => {
                        return {
                            value: variant.id,
                            label: variant.title + (!variant.hasStock ? '(Sold Out)' : ''),
                            disabled: !variant.hasStock,
                        }
                    }) }"
                    @change="${ this._updateActiveVariant }">
                </select-input>
            </div>
        `;
    }

    // Getters

    get _soldOut() {
        return this.variants.findIndex((variant) => variant.hasStock) < 0;
    }

	get _availableForPurchase() {
		return (this._activeVariant)
			? this._activeVariant.availableForPurchase
			: this._defaultVariant.availableForPurchase;
    }

    get _defaultVariant() {
        return this.variants.find((variant) => variant.isDefault);
    }

    // Methods

    private _updateActiveVariant(e: Event) {
        const id = (e.target as HTMLSelectElement).value;
        if (id) {
            this._activeVariant = this.variants.find((variant) => variant.id == id);
        }
    }

    protected async _submit() {
        this._busy = true;
        await cartStore.updateCart(this._formData);
        this._busy = false;
    }

}